import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Pressable,
  StyleSheet,
  Platform,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import TrainingCalendarDayDots from './TrainingCalendarDayDots';
import TrainingCalendarExerciseFilters from './TrainingCalendarExerciseFilters';
import { getApiUrl } from '../utils/apiConfig';
import {
  applyCalendarFilters,
  createEmptyCalendarFilters,
  parseAssignedCourses,
} from '../utils/trainingCalendarFilters';
import {
  isoDateToDisplay,
  isoDateToLongDisplay,
  isoFromParts,
  localTodayIsoDate,
  normalizeIsoDateString,
  parseIsoToParts,
} from '../utils/dateFormat';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const SESSION_DAY_BG_LIGHT = '#f9ffd6';
const SESSION_DAY_BG_DARK = 'rgba(253, 253, 10, 0.18)';
const CALENDAR_CREAM = '#faf8f4';

function getDayNumberStyles({ styles, theme, isToday, isSelected, hasSessions }) {
  const result = [styles.dayCellText];
  const highlighted = isToday || isSelected;
  if (highlighted && theme.isDarkMode) {
    result.push(styles.dayCellTextTodayDark);
  } else if (highlighted) {
    result.push(styles.dayCellTextToday);
  } else if (hasSessions) {
    result.push(styles.dayCellTextHasSessions);
  }
  return result;
}

function buildMonthCells(year, monthIndex) {
  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function SessionDateCalendarModal({
  visible,
  onClose,
  selectedIso,
  onSelectDate,
  isUSUser = false,
  studentId = null,
  assignedCourses: assignedCoursesProp = null,
}) {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const todayIso = localTodayIsoDate();

  const initial = parseIsoToParts(selectedIso) || parseIsoToParts(todayIso);
  const [viewYear, setViewYear] = useState(initial?.year ?? new Date().getFullYear());
  const [viewMonthIndex, setViewMonthIndex] = useState(
    initial?.monthIndex ?? new Date().getMonth()
  );
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [sessionCountsByDate, setSessionCountsByDate] = useState({});
  const [courseTypesByDate, setCourseTypesByDate] = useState({});
  const [coursesByDate, setCoursesByDate] = useState({});
  const [calendarFilters, setCalendarFilters] = useState(createEmptyCalendarFilters);
  const [assignedCoursesLocal, setAssignedCoursesLocal] = useState([]);

  useEffect(() => {
    if (!visible) {
      setCalendarFilters(createEmptyCalendarFilters());
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const parts = parseIsoToParts(selectedIso) || parseIsoToParts(todayIso);
    if (parts) {
      setViewYear(parts.year);
      setViewMonthIndex(parts.monthIndex);
    }
  }, [visible, selectedIso, todayIso]);

  const loadCalendar = useCallback(async () => {
    if (!studentId || !visible) {
      setSessionCountsByDate({});
      setCourseTypesByDate({});
      return;
    }
    setLoadingCalendar(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/students/${encodeURIComponent(String(studentId))}/training-calendar?year=${viewYear}&month=${viewMonthIndex + 1}`
      );
      if (res.ok) {
        const data = await res.json();
        setSessionCountsByDate(data.session_counts_by_date || {});
        setCourseTypesByDate(data.course_types_by_date || {});
        setCoursesByDate(data.courses_by_date || {});
      } else {
        setSessionCountsByDate({});
        setCourseTypesByDate({});
        setCoursesByDate({});
      }
    } catch {
      setSessionCountsByDate({});
      setCourseTypesByDate({});
      setCoursesByDate({});
    } finally {
      setLoadingCalendar(false);
    }
  }, [studentId, visible, viewYear, viewMonthIndex]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  useEffect(() => {
    if (Array.isArray(assignedCoursesProp)) {
      setAssignedCoursesLocal(assignedCoursesProp);
      return;
    }
    if (!studentId || !visible) {
      setAssignedCoursesLocal([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/users/${encodeURIComponent(String(studentId))}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setAssignedCoursesLocal(parseAssignedCourses(data?.courses));
      } catch {
        if (!cancelled) setAssignedCoursesLocal([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignedCoursesProp, studentId, visible]);

  const filteredCalendar = useMemo(
    () =>
      applyCalendarFilters({
        sessionCountsByDate,
        courseTypesByDate,
        coursesByDate,
        filters: calendarFilters,
      }),
    [sessionCountsByDate, courseTypesByDate, coursesByDate, calendarFilters]
  );
  const displaySessionCountsByDate = filteredCalendar.sessionCountsByDate;
  const displayCourseTypesByDate = filteredCalendar.courseTypesByDate;

  const selectedKey = normalizeIsoDateString(selectedIso);
  const cells = useMemo(
    () => buildMonthCells(viewYear, viewMonthIndex),
    [viewYear, viewMonthIndex]
  );

  const sheetWidth = Math.min(380, Math.max(280, windowWidth - 32));
  const dayCellSize = Math.floor((sheetWidth - 32) / 7);
  const gold = theme.colors.primary || theme.colors.heading || '#d4af37';
  const styles = getStyles(theme, sheetWidth, dayCellSize, gold);

  const goPrevMonth = () => {
    if (viewMonthIndex === 0) {
      setViewMonthIndex(11);
      setViewYear((y) => y - 1);
      return;
    }
    setViewMonthIndex((m) => m - 1);
  };

  const goNextMonth = () => {
    if (viewMonthIndex === 11) {
      setViewMonthIndex(0);
      setViewYear((y) => y + 1);
      return;
    }
    setViewMonthIndex((m) => m + 1);
  };

  const handlePickDay = (day) => {
    if (!day) return;
    const iso = isoFromParts(viewYear, viewMonthIndex, day);
    onSelectDate(iso);
    onClose();
  };

  const headerSelectedLabel = selectedKey ? isoDateToLongDisplay(selectedKey) : '—';

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel="Close calendar" />
        <View
          style={styles.sheet}
          {...(Platform.OS === 'web' ? { dataSet: { sessionDateCalendar: 'true' } } : {})}
        >
          <Text style={styles.sheetTitle}>Select training date</Text>
          <Text style={styles.sheetSelected}>
            Selected: {headerSelectedLabel}
          </Text>
          <Text style={styles.sheetLegend}>
            Yellow days have logged sessions. Dots use My Courses colours: External (yellow),
            Internal (red), Weapons (purple), Dim Mak (orange), Fitness (blue).
          </Text>

          <TrainingCalendarExerciseFilters
            assignedCourses={assignedCoursesLocal}
            filters={calendarFilters}
            onFiltersChange={setCalendarFilters}
            onRefresh={() => setCalendarFilters(createEmptyCalendarFilters())}
            compact
          />

          <View style={styles.monthNavRow}>
            <TouchableOpacity
              style={styles.monthNavButton}
              onPress={goPrevMonth}
              accessibilityLabel="Previous month"
            >
              <MaterialIcons name="chevron-left" size={28} color={gold} />
            </TouchableOpacity>
            <Text style={styles.monthNavLabel}>
              {MONTH_NAMES[viewMonthIndex]} {viewYear}
            </Text>
            <TouchableOpacity
              style={styles.monthNavButton}
              onPress={goNextMonth}
              accessibilityLabel="Next month"
            >
              <MaterialIcons name="chevron-right" size={28} color={gold} />
            </TouchableOpacity>
          </View>

          <View style={styles.weekdayRow}>
            {WEEKDAY_LABELS.map((label) => (
              <Text key={label} style={styles.weekdayLabel}>
                {label}
              </Text>
            ))}
          </View>

          {loadingCalendar && studentId ? (
            <ActivityIndicator size="small" color={gold} style={styles.calendarLoader} />
          ) : null}

          <View
            style={styles.daysGrid}
            {...(Platform.OS === 'web' ? { dataSet: { sessionDateCalendarGrid: 'true' } } : {})}
          >
            {cells.map((day, index) => {
              if (day == null) {
                return (
                  <View
                    key={`empty-${index}`}
                    style={[styles.dayCell, !theme.isDarkMode && styles.dayCellEmptyLight]}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { sessionDateCalendarEmptyDay: 'true' } }
                      : {})}
                  />
                );
              }
              const iso = isoFromParts(viewYear, viewMonthIndex, day);
              const sessionCount = displaySessionCountsByDate[iso] || 0;
              const dayCourseTypes = displayCourseTypesByDate[iso] || [];
              const hasSessions = sessionCount > 0;
              const isToday = iso === todayIso;
              const isSelected = iso === selectedKey;
              return (
                <TouchableOpacity
                  key={`day-${iso}`}
                  style={[
                    styles.dayCell,
                    styles.dayCellButton,
                    (isToday || isSelected) && styles.dayCellHighlightBorder,
                    hasSessions &&
                      (theme.isDarkMode
                        ? styles.dayCellHasSessionsDark
                        : styles.dayCellHasSessionsLight),
                  ]}
                  onPress={() => handlePickDay(day)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel={
                    hasSessions
                      ? `${isoDateToDisplay(iso, isUSUser)}, ${sessionCount} session(s)`
                      : isoDateToDisplay(iso, isUSUser)
                  }
                  {...(Platform.OS === 'web'
                    ? {
                        dataSet: {
                          sessionDateCalendarDay: 'true',
                          ...(isToday ? { sessionDateCalendarToday: 'true' } : {}),
                          ...(hasSessions ? { sessionDateCalendarHasSessions: 'true' } : {}),
                          ...(isSelected ? { sessionDateCalendarSelected: 'true' } : {}),
                        },
                      }
                    : {})}
                >
                  <Text
                    style={getDayNumberStyles({
                      styles,
                      theme,
                      isToday,
                      isSelected,
                      hasSessions,
                    })}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { sessionDateCalendarDayNumber: 'true' } }
                      : {})}
                  >
                    {day}
                  </Text>
                  {hasSessions ? (
                    <TrainingCalendarDayDots
                      courseTypes={dayCourseTypes}
                      fallbackSessionCount={sessionCount}
                    />
                  ) : null}
                </TouchableOpacity>
              );
            })}
          </View>

          <TouchableOpacity style={styles.closeButton} onPress={onClose} activeOpacity={0.8}>
            <Text style={styles.closeButtonText}>Close</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

function getStyles(theme, sheetWidth, dayCellSize, gold) {
  const surface = theme.isDarkMode ? theme.colors.surface : CALENDAR_CREAM;
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
    },
    sheet: {
      width: sheetWidth,
      maxWidth: '100%',
      backgroundColor: surface,
      borderRadius: 12,
      borderWidth: theme.isDarkMode ? 2 : 3,
      borderColor: theme.isDarkMode ? theme.colors.border : gold,
      padding: 16,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }
        : {}),
    },
    sheetTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading,
      textAlign: 'center',
      marginBottom: 6,
    },
    sheetSelected: {
      fontSize: 14,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 4,
      opacity: 0.9,
    },
    sheetLegend: {
      fontSize: 12,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 12,
      opacity: 0.75,
      lineHeight: 16,
    },
    monthNavRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
      backgroundColor: theme.isDarkMode ? 'transparent' : CALENDAR_CREAM,
    },
    monthNavButton: {
      padding: 4,
      minWidth: 40,
      alignItems: 'center',
    },
    monthNavLabel: {
      flex: 1,
      textAlign: 'center',
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
    },
    weekdayRow: {
      flexDirection: 'row',
      marginBottom: 6,
      backgroundColor: theme.isDarkMode ? 'transparent' : CALENDAR_CREAM,
    },
    weekdayLabel: {
      flex: 1,
      textAlign: 'center',
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.text,
      opacity: 0.65,
    },
    calendarLoader: {
      marginBottom: 8,
    },
    daysGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      backgroundColor: theme.isDarkMode ? 'transparent' : CALENDAR_CREAM,
    },
    dayCell: {
      width: dayCellSize,
      minHeight: dayCellSize + 6,
      alignItems: 'center',
      justifyContent: 'flex-start',
      paddingTop: 4,
    },
    dayCellEmptyLight: {
      backgroundColor: CALENDAR_CREAM,
    },
    dayCellButton: {
      borderRadius: 8,
      borderWidth: 2,
      borderColor: 'transparent',
      backgroundColor: theme.isDarkMode ? 'transparent' : CALENDAR_CREAM,
    },
    dayCellHighlightBorder: {
      borderColor: gold,
      borderWidth: 2,
    },
    dayCellHasSessionsLight: {
      backgroundColor: SESSION_DAY_BG_LIGHT,
    },
    dayCellHasSessionsDark: {
      backgroundColor: SESSION_DAY_BG_DARK,
    },
    dayCellText: {
      fontSize: 14,
      fontWeight: '500',
      color: theme.colors.text,
    },
    dayCellTextToday: {
      fontWeight: '700',
      color: theme.colors.heading,
    },
    dayCellTextTodayDark: {
      fontWeight: '700',
      color: '#ffffff',
    },
    dayCellTextHasSessions: {
      fontWeight: '700',
      color: theme.colors.heading || gold,
    },
    closeButton: {
      marginTop: 14,
      alignSelf: 'center',
      paddingVertical: 10,
      paddingHorizontal: 24,
      borderRadius: 8,
      backgroundColor: '#6b7280',
    },
    closeButtonText: {
      color: '#FFFFFF',
      fontSize: 15,
      fontWeight: '600',
    },
  });
}
